import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Info } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import 'shepherd.js/dist/css/shepherd.css'
import './tour.css'
import { WelcomeModal } from './WelcomeModal'
import { useLang } from '../_metronic/i18n/Metronici18n'
import { userApi } from '../services/api/user.api'
import {
  MOCK_TARGET_LIST_RESPONSE,
  MOCK_WORKFLOWS_RESPONSE,
  MOCK_WORKFLOW_FILTER_OPTIONS,
} from './mock/mockCreateScanData'
import { MOCK_SCAN_LIST_RESPONSE, MOCK_SCAN_CHART_RESPONSE } from './mock/mockScanHistory'
import { MOCK_VULNERABILITIES_RESPONSE, MOCK_VULNERABILITY_FILTER_OPTIONS } from './mock/mockVulnerabilities'
import { MOCK_ASSETS_RESPONSE, MOCK_ASSET_FILTER_OPTIONS, MOCK_ASSET_DETAIL } from './mock/mockAssets'
import { MOCK_IPS_RESPONSE, MOCK_IP_FILTER_OPTIONS, MOCK_IP_DETAIL } from './mock/mockIPs'
import { MOCK_SSL_RESPONSE, MOCK_SSL_FILTER_OPTIONS, MOCK_SSL_DETAIL } from './mock/mockSSL'
import { MOCK_TICKETS_RESPONSE, MOCK_TICKET_FILTER_OPTIONS, MOCK_TICKET_DETAIL } from './mock/mockTickets'
import { MOCK_REPORT_HISTORY_INFINITE, MOCK_REPORT_TEMPLATES } from './mock/mockReports'
import { MOCK_INTEGRATIONS_RESPONSE } from './mock/mockIntegrations'
import { MOCK_DNS_RESPONSE } from './mock/mockDNS'
import { MOCK_SMTP_RESPONSE } from './mock/mockSMTP'
import { seedChatList, clearChatListSeed, mockChatListKey } from './mock/mockChat'
import { MOCK_EMAILS_RESPONSE, MOCK_EMAIL_FILTER_OPTIONS } from './mock/mockEmails'
import { MOCK_DNS_RECORDS_RESPONSE, MOCK_DNS_RECORD_FILTER_OPTIONS } from './mock/mockDNSRecords'
import { MOCK_PORTS_RESPONSE, MOCK_PORT_FILTER_OPTIONS } from './mock/mockPorts'
import { MOCK_LEAKED_HOSTS_RESPONSE } from './mock/mockLeaked'
import { MOCK_WORKFLOW_LIST_RESPONSE, MOCK_WORKFLOW_LIST_FILTER_OPTIONS } from './mock/mockWorkflow'
import { MOCK_SCAN_REPORTS_INFINITE } from './mock/mockCreateReport'
import { MOCK_HOST_INSIGHTS, MOCK_IP_INSIGHTS, MOCK_TECH_EXPOSURE } from './mock/mockAssetInsights'
import { useAuthStore } from '../store/authStore'
import { useFeature } from '../Utils/featureFlagEngine'
import { queryKeys } from '../lib/react-query/queryKeys'

export type TourId =
  | 'onboarding'
  | 'dashboard'
  | 'vulnerabilities'
  | 'scanHistory'
  | 'attackSurface'
  | 'asDomains'
  | 'asIPs'
  | 'asPorts'
  | 'asDNS'
  | 'asSSL'
  | 'asEmail'
  | 'asLeaked'
  | 'workflow'
  | 'createScan'
  | 'scanDetails'
  | 'assetDetails'
  | 'ipDetails'
  | 'sslDetails'
  | 'vulnDetails'
  | 'integrations'
  | 'reportsHistory'
  | 'reportTemplates'
  | 'templateGenerator'
  | 'createReport'
  | 'tickets'
  | 'ticketDetails'
  | 'orgDashboard'
  | 'aiChat'
  | 'workflowLibrary'
  | 'organization'
  | 'settings'
  | 'security'
  | 'dnsInspect'
  | 'smtp'

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface TourContextValue {
  startTour: (tourId: TourId, seedFn?: () => void) => void
  endTour: () => void
  isTourActive: boolean
  isMockTour: boolean
  activeTourId: TourId | null
  getSeedFn: (tourId: TourId) => (() => void) | undefined
}

const TourContext = createContext<TourContextValue | null>(null)

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used inside TourProvider')
  return ctx
}

// Pre-steps 0-3 are shown as React modals before Shepherd takes over.
// 0: welcome/confirm  1: sidebar hint  2: library button hint  3: WF library intro
// Step 2 is skipped when the user lacks workflow_library permission.
const LAST_PRE_STEP = 3

// Query keys to clear when a mock tour ends, keyed by tourId
function getMockQueryKeys(tourId: TourId, orgId: string): unknown[][] {
  switch (tourId) {
    case 'scanHistory':
      return [[...queryKeys.scans.list(orgId, '')], [...queryKeys.scans.chart(orgId)]]
    case 'vulnerabilities':
      return [[...queryKeys.vulnerabilities.list(orgId, '')], [...queryKeys.vulnerabilities.filterOptions(orgId)]]
    case 'asDomains':
      return [[...queryKeys.assets.list(orgId, '')], [...queryKeys.assets.filterOptions(orgId)]]
    case 'assetDetails':
      return [
        [...queryKeys.assets.detail(orgId, 'mock-asset-1')],
        [...queryKeys.assetInsights.detail(orgId, 'host', 'mock-asset-1')],
        [...queryKeys.assetInsights.techExposure(orgId, 'host', 'mock-asset-1')],
      ]
    case 'asIPs':
      return [[...queryKeys.ips.list(orgId, '')], [...queryKeys.ips.filterOptions(orgId)]]
    case 'ipDetails':
      return [
        [...queryKeys.ips.detail(orgId, 'mock-ip-1')],
        [...queryKeys.assetInsights.detail(orgId, 'ip', 'mock-ip-1')],
        [...queryKeys.assetInsights.techExposure(orgId, 'ip', 'mock-ip-1')],
      ]
    case 'asSSL':
      return [[...queryKeys.ssl.list(orgId, '')], [...queryKeys.ssl.filterOptions(orgId)]]
    case 'sslDetails':
      return [[...queryKeys.ssl.detail(orgId, 'mock-ssl-1')]]
    case 'tickets':
      return [[...queryKeys.tickets.list(orgId, '')], [...queryKeys.tickets.filterOptions(orgId)]]
    case 'ticketDetails':
      return [[...queryKeys.tickets.detail(orgId, 'mock-ticket-1')]]
    case 'reportsHistory':
      return [[...queryKeys.reports.historyInfinite(orgId, '')]]
    case 'reportTemplates':
      return [[...queryKeys.reports.templates(orgId, '')]]
    case 'integrations':
      return [[...queryKeys.integrations.list(orgId, '')]]
    case 'dnsInspect':
      return [[...queryKeys.dns.list(orgId)]]
    case 'smtp':
      return [[...queryKeys.smtp.list(orgId, '')]]
    case 'aiChat':
      // Shares one definition with `seedAiChat` — see `mockChatListKey`.
      return [[...mockChatListKey(orgId)]]
    case 'asEmail':
      return [[...queryKeys.emails.list(orgId, '')], [...queryKeys.emails.filterOptions(orgId)]]
    case 'asDNS':
      return [[...queryKeys.dnsRecords.list(orgId, '')], [...queryKeys.dnsRecords.filterOptions(orgId)]]
    case 'asPorts':
      return [[...queryKeys.ports.list(orgId, '')], [...queryKeys.ports.filterOptions(orgId)]]
    case 'asLeaked':
      return [[...queryKeys.leaked.hosts(orgId, 1, '')]]
    case 'workflow':
      return [[...queryKeys.workflow.list(orgId, '')], [...queryKeys.workflow.filterOptions(orgId)]]
    case 'createReport':
      return [[...queryKeys.reports.scanReportsInfinite(orgId)]]
    case 'createScan':
      return [
        [orgId, 'scans', 'scannableAssets', 1, ''],
        [orgId, 'scans', 'workflowsForScan', 1, '', JSON.stringify({ tags: [], status: [], entry: [] }), ''],
      ]
    default:
      return []
  }
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const lang = useLang()?.lang.selectedLang ?? 'en'
  const queryClient = useQueryClient()
  const intl = useIntl()
  const { hasPermission } = useFeature()
  const hasLibrary = hasPermission('workflow_library')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tourRef = useRef<any>(null)
  const [isTourActive, setIsTourActive] = useState(false)
  const [isMockTour, setIsMockTour] = useState(false)
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null)
  // null = Shepherd is running, number = showing React pre-step
  const [preStep, setPreStep] = useState<number | null>(null)

  // Refs so endTour always reads current values regardless of closure age
  const isMockTourRef = useRef(false)
  const activeTourIdRef = useRef<TourId | null>(null)

  const endTour = useCallback(() => {
    setIsTourActive(false)
    setPreStep(null)
    const tid = activeTourIdRef.current
    const wasMock = isMockTourRef.current
    activeTourIdRef.current = null
    isMockTourRef.current = false
    setActiveTourId(null)
    setIsMockTour(false)
    if (tourRef.current) {
      const t = tourRef.current
      tourRef.current = null
      // Remove listeners before cancelling to prevent re-entrant endTour calls
      t.off('complete', endTour)
      t.off('cancel', endTour)
      try { t.cancel() } catch (_) { }
    }
    const orgId = useAuthStore.getState().orgId
    if (tid === 'onboarding') {
      userApi.markUserNotNew().catch(() => { })
      // Restore normal query behaviour for scan queries after tour ends
      if (orgId) {
        queryClient.setQueryDefaults([orgId, 'scans', 'scannableAssets'], {})
        queryClient.setQueryDefaults([orgId, 'scans', 'workflowsForScan'], {})
        queryClient.removeQueries({
          predicate: (q) => {
            const k = q.queryKey as string[]
            return k[0] === orgId && k[1] === 'scans' && (k[2] === 'scannableAssets' || k[2] === 'workflowsForScan')
          }
        })
      }
    } else if (wasMock && tid && orgId) {
      // `aiChat` is the one seed that also FROZE its key against refetching —
      // the chat list's stale time is 0, so nothing else would survive a mount
      // (see `seedChatList`). The generic loop below clears data but not query
      // defaults, and leaving `staleTime: Infinity` behind would freeze the
      // user's real chat list for the rest of the session.
      if (tid === 'aiChat') clearChatListSeed(queryClient, orgId)
      // Clear mock data for all active subscribers, then invalidate so they re-fetch
      const keys = getMockQueryKeys(tid, orgId)
      for (const key of keys) {
        queryClient.setQueryData(key, undefined)
        queryClient.invalidateQueries({ queryKey: key })
      }
      // Detail-page tours navigate to a mock URL — go back to the list page
      const mockDetailReturn: Partial<Record<TourId, string>> = {
        assetDetails: '/attack-surface-explore/Domains',
        ipDetails: '/attack-surface-explore/IP',
        sslDetails: '/attack-surface-explore/SSL',
        ticketDetails: '/tickets',
      }
      if (mockDetailReturn[tid]) {
        navigate(mockDetailReturn[tid]!, { replace: true })
      }
    }
    // Post-tour DOM resets (run regardless of mock/real)
    if (tid === 'createScan') {
      document.querySelector<HTMLElement>('[data-tour="goto-scan-step-1"]')?.click()
    }
  }, [queryClient, navigate])

  const seedCreateScanMock = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return

    // Prevent React Query from refetching these keys during the tour
    queryClient.setQueryDefaults([orgId, 'scans', 'scannableAssets'], { staleTime: Infinity, gcTime: Infinity })
    queryClient.setQueryDefaults([orgId, 'scans', 'workflowsForScan'], { staleTime: Infinity, gcTime: Infinity })

    // Keys must exactly match what useScannableAssets and useWorkflowsForScan produce:
    // scannableAssets: [orgId, 'scans', 'scannableAssets', page, search]
    // workflowsForScan: [orgId, 'scans', 'workflowsForScan', page, search, filterKey, portType]
    const defaultFilterKey = JSON.stringify({ tags: [], status: [], entry: [] })
    for (let page = 1; page <= 3; page++) {
      queryClient.setQueryData([orgId, 'scans', 'scannableAssets', page, ''], MOCK_TARGET_LIST_RESPONSE)
      queryClient.setQueryData([orgId, 'scans', 'workflowsForScan', page, '', defaultFilterKey, ''], MOCK_WORKFLOWS_RESPONSE)
    }
    queryClient.setQueryData([orgId, 'workflow', 'filterOptions'], MOCK_WORKFLOW_FILTER_OPTIONS)
  }, [queryClient])

  // ─── Per-tour mock seed functions ──────────────────────────────────────────

  const seedScanHistory = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.scans.list(orgId, ''), MOCK_SCAN_LIST_RESPONSE)
    queryClient.setQueryData(queryKeys.scans.chart(orgId), MOCK_SCAN_CHART_RESPONSE)
  }, [queryClient])

  const seedVulnerabilities = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.vulnerabilities.list(orgId, ''), MOCK_VULNERABILITIES_RESPONSE)
    queryClient.setQueryData(queryKeys.vulnerabilities.filterOptions(orgId), MOCK_VULNERABILITY_FILTER_OPTIONS)
  }, [queryClient])

  const seedAsDomains = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.assets.list(orgId, ''), MOCK_ASSETS_RESPONSE)
    queryClient.setQueryData(queryKeys.assets.filterOptions(orgId), MOCK_ASSET_FILTER_OPTIONS)
  }, [queryClient])

  const seedAssetDetails = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    // The wrapper still reads identity (host name) and `.value` (Business
    // value) off the detail endpoint's own cache entry.
    queryClient.setQueryData(queryKeys.assets.detail(orgId, 'mock-asset-1'), MOCK_ASSET_DETAIL)
    queryClient.setQueryData(queryKeys.assetInsights.detail(orgId, 'host', 'mock-asset-1'), MOCK_HOST_INSIGHTS)
    queryClient.setQueryData(queryKeys.assetInsights.techExposure(orgId, 'host', 'mock-asset-1'), MOCK_TECH_EXPOSURE)
    navigate('/asset-details?id=mock-asset-1', { replace: true })
  }, [queryClient, navigate])

  const seedAsIPs = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.ips.list(orgId, ''), MOCK_IPS_RESPONSE)
    queryClient.setQueryData(queryKeys.ips.filterOptions(orgId), MOCK_IP_FILTER_OPTIONS)
  }, [queryClient])

  const seedIpDetails = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.ips.detail(orgId, 'mock-ip-1'), MOCK_IP_DETAIL)
    queryClient.setQueryData(queryKeys.assetInsights.detail(orgId, 'ip', 'mock-ip-1'), MOCK_IP_INSIGHTS)
    queryClient.setQueryData(queryKeys.assetInsights.techExposure(orgId, 'ip', 'mock-ip-1'), MOCK_TECH_EXPOSURE)
    navigate('/ip-details?id=mock-ip-1', { replace: true })
  }, [queryClient, navigate])

  const seedAsSSL = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.ssl.list(orgId, ''), MOCK_SSL_RESPONSE)
    queryClient.setQueryData(queryKeys.ssl.filterOptions(orgId), MOCK_SSL_FILTER_OPTIONS)
  }, [queryClient])

  const seedSslDetails = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.ssl.detail(orgId, 'mock-ssl-1'), MOCK_SSL_DETAIL)
    navigate('/ssl-details?id=mock-ssl-1', { replace: true })
  }, [queryClient, navigate])

  const seedTickets = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.tickets.list(orgId, ''), MOCK_TICKETS_RESPONSE)
    queryClient.setQueryData(queryKeys.tickets.filterOptions(orgId), MOCK_TICKET_FILTER_OPTIONS)
  }, [queryClient])

  const seedTicketDetails = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.tickets.detail(orgId, 'mock-ticket-1'), MOCK_TICKET_DETAIL)
    navigate('/ticket-details?id=mock-ticket-1', { replace: true })
  }, [queryClient, navigate])

  const seedReportsHistory = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.reports.historyInfinite(orgId, ''), MOCK_REPORT_HISTORY_INFINITE)
  }, [queryClient])

  const seedReportTemplates = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.reports.templates(orgId, ''), MOCK_REPORT_TEMPLATES)
  }, [queryClient])

  const seedIntegrations = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.integrations.list(orgId, ''), MOCK_INTEGRATIONS_RESPONSE)
  }, [queryClient])

  const seedDnsInspect = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.dns.list(orgId), MOCK_DNS_RESPONSE)
  }, [queryClient])

  const seedSmtp = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.smtp.list(orgId, ''), MOCK_SMTP_RESPONSE)
  }, [queryClient])

  const seedAiChat = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    seedChatList(queryClient, orgId)
  }, [queryClient])

  const seedAsEmail = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.emails.list(orgId, ''), MOCK_EMAILS_RESPONSE)
    queryClient.setQueryData(queryKeys.emails.filterOptions(orgId), MOCK_EMAIL_FILTER_OPTIONS)
  }, [queryClient])

  const seedAsDNS = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.dnsRecords.list(orgId, ''), MOCK_DNS_RECORDS_RESPONSE)
    queryClient.setQueryData(queryKeys.dnsRecords.filterOptions(orgId), MOCK_DNS_RECORD_FILTER_OPTIONS)
  }, [queryClient])

  const seedAsPorts = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.ports.list(orgId, ''), MOCK_PORTS_RESPONSE)
    queryClient.setQueryData(queryKeys.ports.filterOptions(orgId), MOCK_PORT_FILTER_OPTIONS)
  }, [queryClient])

  const seedAsLeaked = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.leaked.hosts(orgId, 1, ''), MOCK_LEAKED_HOSTS_RESPONSE)
  }, [queryClient])

  const seedWorkflow = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.workflow.list(orgId, ''), MOCK_WORKFLOW_LIST_RESPONSE)
    queryClient.setQueryData(queryKeys.workflow.filterOptions(orgId), MOCK_WORKFLOW_LIST_FILTER_OPTIONS)
  }, [queryClient])

  const seedCreateReport = useCallback(() => {
    const orgId = useAuthStore.getState().orgId
    if (!orgId) return
    queryClient.setQueryData(queryKeys.reports.scanReportsInfinite(orgId), MOCK_SCAN_REPORTS_INFINITE)
  }, [queryClient])

  const getSeedFn = useCallback((tourId: TourId): (() => void) | undefined => {
    const map: Partial<Record<TourId, () => void>> = {
      scanHistory: seedScanHistory,
      vulnerabilities: seedVulnerabilities,
      asDomains: seedAsDomains,
      assetDetails: seedAssetDetails,
      asIPs: seedAsIPs,
      ipDetails: seedIpDetails,
      asSSL: seedAsSSL,
      sslDetails: seedSslDetails,
      tickets: seedTickets,
      ticketDetails: seedTicketDetails,
      reportsHistory: seedReportsHistory,
      reportTemplates: seedReportTemplates,
      integrations: seedIntegrations,
      dnsInspect: seedDnsInspect,
      smtp: seedSmtp,
      aiChat: seedAiChat,
      asEmail: seedAsEmail,
      asDNS: seedAsDNS,
      asPorts: seedAsPorts,
      asLeaked: seedAsLeaked,
      workflow: seedWorkflow,
      createReport: seedCreateReport,
      createScan: seedCreateScanMock,
    }
    return map[tourId]
  }, [
    seedScanHistory, seedVulnerabilities, seedAsDomains, seedAssetDetails,
    seedAsIPs, seedIpDetails, seedAsSSL, seedSslDetails,
    seedTickets, seedTicketDetails, seedReportsHistory, seedReportTemplates,
    seedIntegrations, seedDnsInspect, seedSmtp, seedAiChat,
    seedAsEmail, seedAsDNS, seedAsPorts, seedAsLeaked, seedWorkflow, seedCreateReport, seedCreateScanMock,
  ])

  // ───────────────────────────────────────────────────────────────────────────

  const startShepherd = useCallback(async (tourId: TourId) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const moduleMap: Record<TourId, () => Promise<any>> = {
      onboarding: () => import('./tours/onboarding'),
      dashboard: () => import('./tours/dashboard'),
      vulnerabilities: () => import('./tours/vulnerabilities'),
      scanHistory: () => import('./tours/scanHistory'),
      attackSurface: () => import('./tours/attackSurface'),
      asDomains: () => import('./tours/asDomains'),
      asIPs: () => import('./tours/asIPs'),
      asPorts: () => import('./tours/asPorts'),
      asDNS: () => import('./tours/asDNS'),
      asSSL: () => import('./tours/asSSL'),
      asEmail: () => import('./tours/asEmail'),
      asLeaked: () => import('./tours/asLeaked'),
      workflow: () => import('./tours/workflow'),
      createScan: () => import('./tours/createScan'),
      scanDetails: () => import('./tours/scanDetails'),
      assetDetails: () => import('./tours/assetDetails'),
      ipDetails: () => import('./tours/ipDetails'),
      sslDetails: () => import('./tours/sslDetails'),
      vulnDetails: () => import('./tours/vulnDetails'),
      integrations: () => import('./tours/integrations'),
      reportsHistory: () => import('./tours/reportsHistory'),
      reportTemplates: () => import('./tours/reportTemplates'),
      templateGenerator: () => import('./tours/templateGenerator'),
      createReport: () => import('./tours/createReport'),
      tickets: () => import('./tours/tickets'),
      ticketDetails: () => import('./tours/ticketDetails'),
      orgDashboard: () => import('./tours/orgDashboard'),
      aiChat: () => import('./tours/aiChat'),
      workflowLibrary: () => import('./tours/workflowLibrary'),
      organization: () => import('./tours/organization'),
      settings: () => import('./tours/settings'),
      security: () => import('./tours/security'),
      dnsInspect: () => import('./tours/dnsInspect'),
      smtp: () => import('./tours/smtp'),
    }
    const mod = await moduleMap[tourId]()
    const factory = mod[`create${capitalize(tourId)}Tour`]
    const tour = tourId === 'onboarding'
      ? factory(navigate, lang, seedCreateScanMock)
      : factory(navigate, lang)
    tour.on('complete', endTour)
    tour.on('cancel', endTour)
    // Scroll the target element to the upper fifth of the viewport on each step.
    // We scroll document.documentElement directly so it works even while the
    // html overflow is locked (user scroll disabled during tour).
    tour.on('show', () => {
      const step = tour.currentStep
      const selector = step?.options?.attachTo?.element
      if (typeof selector !== 'string') return

      const desiredTop = window.innerHeight * 0.2
      const deadline = Date.now() + 5000
      let lastTop: number | null = null
      let stableCount = 0

      const poll = () => {
        if (tour.currentStep !== step) return
        if (Date.now() > deadline) return
        const el = document.querySelector<HTMLElement>(selector)
        if (!el) { requestAnimationFrame(poll); return }
        const top = el.getBoundingClientRect().top
        if (lastTop === null) { lastTop = top; requestAnimationFrame(poll); return }
        if (Math.abs(top - lastTop) < 2) { stableCount++ } else { stableCount = 0 }
        lastTop = top
        if (stableCount < 3) { requestAnimationFrame(poll); return }
        // Direct scrollTop assignment works even with overflow:hidden on html
        const scrollTarget = Math.max(0, document.documentElement.scrollTop + top - desiredTop)
        if (Math.abs(top - desiredTop) > 20) {
          document.documentElement.scrollTop = scrollTarget
        }
      }
      requestAnimationFrame(poll)
    })
    tourRef.current = tour
    setPreStep(null)
    tour.start()
  }, [navigate, lang, endTour, seedCreateScanMock])

  const startTour = useCallback((tourId: TourId, seedFn?: () => void) => {
    if (tourRef.current) tourRef.current.cancel()
    activeTourIdRef.current = tourId
    setActiveTourId(tourId)
    setIsTourActive(true)
    if (seedFn) {
      seedFn()
      isMockTourRef.current = true
      setIsMockTour(true)
    } else {
      isMockTourRef.current = false
      setIsMockTour(false)
    }
    if (tourId === 'onboarding') {
      setPreStep(0)
    } else {
      setPreStep(null)
      startShepherd(tourId)
    }
  }, [startShepherd])

  // Navigate to the right page when entering a pre-step
  const navigateForPreStep = useCallback((step: number) => {
    if (step === 1) navigate('/workFlow', { replace: true })
    if (step === 2) navigate('/workFlow', { replace: true })
    if (step === 3) navigate('/workFlow-library', { replace: true })
  }, [navigate])

  const handlePreNext = useCallback(() => {
    const current = preStep ?? 0
    // Step 2 (library button hint) is skipped when user lacks the permission
    let next = current + 1
    if (next === 2 && !hasLibrary) next = 3

    if (next > LAST_PRE_STEP) {
      startShepherd('onboarding')
    } else {
      navigateForPreStep(next)
      setPreStep(next)
    }
  }, [preStep, hasLibrary, startShepherd, navigateForPreStep])

  const handlePreBack = useCallback(() => {
    const current = preStep ?? 1
    let prev = current - 1
    // Skip step 2 backwards too if no library permission
    if (prev === 2 && !hasLibrary) prev = 1
    prev = Math.max(0, prev)
    navigateForPreStep(prev)
    setPreStep(prev)
  }, [preStep, hasLibrary, navigateForPreStep])


  // Lock page scroll while a tour is active so the user cannot scroll manually
  useEffect(() => {
    const html = document.documentElement
    if (isTourActive) {
      html.style.overflow = 'hidden'
    } else {
      html.style.overflow = ''
    }
    return () => { html.style.overflow = '' }
  }, [isTourActive])

  // Cancel tour if user navigates away from the expected page(s) mid-Shepherd
  useEffect(() => {
    if (!isTourActive || preStep !== null || !tourRef.current) return
    const allowedPathsByTour: Record<TourId, string[]> = {
      onboarding: ['/workFlow-library', '/workFlow', '/create-scan', '/tour/scan-demo'],
      dashboard: ['/dashboard', '/org-dashboard'],
      vulnerabilities: ['/vulnerabilities'],
      scanHistory: ['/scan-history', '/scan-details', '/scan'],
      attackSurface: ['/attack-surface-explore'],
      asDomains: ['/attack-surface-explore/Domains'],
      asIPs: ['/attack-surface-explore/IP'],
      asPorts: ['/attack-surface-explore/Ports'],
      asDNS: ['/attack-surface-explore/DNS Records'],
      asSSL: ['/attack-surface-explore/SSL'],
      asEmail: ['/attack-surface-explore/Email Address'],
      asLeaked: ['/attack-surface-explore/Leaked Data'],
      workflow: ['/workFlow'],
      createScan: ['/create-scan'],
      scanDetails: ['/scan-details', '/scan'],
      assetDetails: ['/asset-details'],
      ipDetails: ['/ip-details'],
      sslDetails: ['/ssl-details'],
      vulnDetails: ['/vulnerabilities'],
      integrations: ['/integrations'],
      reportsHistory: ['/report/history'],
      reportTemplates: ['/report/templates'],
      templateGenerator: ['/report/template-generator'],
      createReport: ['/report/create'],
      tickets: ['/tickets'],
      ticketDetails: ['/ticket-details'],
      orgDashboard: ['/org-dashboard'],
      aiChat: ['/chat-history'],
      workflowLibrary: ['/workFlow-library'],
      organization: ['/organization'],
      settings: ['/crafted/account/settings'],
      security: ['/crafted/account/security'],
      dnsInspect: ['/attack-surface/dns'],
      smtp: ['/attack-surface/smtp'],
    }
    const allowed = activeTourId ? (allowedPathsByTour[activeTourId] ?? []) : []
    const isAllowed = allowed.some(p => location.pathname.startsWith(p))
    if (!isAllowed) tourRef.current.cancel()
  }, [location.pathname, isTourActive, preStep, activeTourId])

  // Apply RTL dir + mirror left→right for FA mode via MutationObserver.
  // FloatingUI sets inline left:Xpx on every autoUpdate; we convert to right
  // synchronously in the mutation callback, before the browser paints.
  useEffect(() => {
    if (!isTourActive || preStep !== null) return

    const fixEl = (el: HTMLElement) => {
      el.setAttribute('dir', lang === 'fa' ? 'rtl' : 'ltr')
      if (lang === 'fa') {
        // Skip elements manually centered via transform (e.g. the done step)
        if (el.style.transform && el.style.transform.includes('translate')) return
        const left = parseFloat(el.style.left)
        if (!isNaN(left)) {
          el.style.left = ''
          el.style.right = `${window.innerWidth - left - el.offsetWidth}px`
        }
      }
    }

    // Fix any already-present elements
    document.querySelectorAll<HTMLElement>('.shepherd-element').forEach(fixEl)

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Attribute change on an existing shepherd-element (FloatingUI updating left)
        if (m.type === 'attributes' && m.target instanceof HTMLElement) {
          const el = m.target
          if (el.classList.contains('shepherd-element')) {
            fixEl(el)
          }
        }
        // New shepherd-element added to DOM
        if (m.type === 'childList') {
          m.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              if (node.classList.contains('shepherd-element')) fixEl(node)
              node.querySelectorAll<HTMLElement>('.shepherd-element').forEach(fixEl)
            }
          })
        }
      }
    })

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style'],
    })

    return () => observer.disconnect()
  }, [lang, isTourActive, preStep])

  const dir = lang === 'fa' ? 'rtl' : 'ltr'
  const user = useAuthStore((state) => state.userProfile?.requires_organization_setup)
  return (
    <TourContext.Provider value={{ startTour, endTour, isTourActive, isMockTour, activeTourId, getSeedFn }}>
      {children}
      {!user && isTourActive && preStep !== null && (
        <WelcomeModal
          language={lang as 'en' | 'fa'}
          stepIndex={preStep}
          hasBack={preStep > 0}
          onNext={handlePreNext}
          onBack={handlePreBack}
          onSkip={endTour}
        />
      )}
      {isTourActive && isMockTour && (
        <div
          dir={dir}
          className='d-flex align-items-center gap-2 mb-0 px-4 py-2 border-0 border-top border-warning rounded-0 alert alert-warning'
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }}
        >
          <Info size={16} className='flex-shrink-0 text-warning' />
          <span className='text-warning-emphasis fw-semibold'>
            {intl.formatMessage({ id: 'Tour is running with demo data. Your real data will appear once scans are complete.' })}
          </span>
          <button
            type='button'
            className='flex-shrink-0 btn-close ms-auto'
            onClick={endTour}
            aria-label='Close tour'
          />
        </div>
      )}
    </TourContext.Provider>
  )
}
